import {
  analytics,
  isAnalyticsReady,
  analyticsConfig,
  getAnalyticsStatus,
} from "@/services/analytics";

/**
 * Enhanced analytics repository with error handling and privacy controls
 *
 * Provides a safe interface for tracking events while respecting user privacy
 * and handling initialization failures gracefully.
 */

export const analyticsRepository = {
  /**
   * Logs an analytics event with enhanced error handling
   */
  logAnalyticsEvent: (eventName: string, eventParams?: object) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log(`🚫 Analytics event blocked: ${eventName}`, {
            status: getAnalyticsStatus(),
            params: eventParams,
          });
        }
        return;
      }

      analytics.track(eventName, eventParams);

      if (analyticsConfig.debugEnabled) {
        console.log(`📊 Analytics event tracked: ${eventName}`, eventParams);
      }
    } catch (error) {
      console.error("Failed to log analytics event:", eventName, error);
    }
  },

  /**
   * Sets analytics user ID with enhanced error handling
   */
  setAnalyticsUserId: (userId: string) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log("🚫 Analytics user ID update blocked:", {
            status: getAnalyticsStatus(),
          });
        }
        return;
      }

      if (userId === "") {
        analytics.reset();
        if (analyticsConfig.debugEnabled) {
          console.log("📊 Analytics user reset");
        }
        return;
      }

      analytics.identify(userId);

      if (analyticsConfig.debugEnabled) {
        console.log("📊 Analytics user identified:", userId);
      }
    } catch (error) {
      console.error("Failed to set analytics user ID:", userId, error);
    }
  },

  /**
   * Sets user property with enhanced error handling
   */
  setAnalyticsUserProperty: (key: string, value: string) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log(`🚫 Analytics user property update blocked: ${key}`, {
            status: getAnalyticsStatus(),
            value,
          });
        }
        return;
      }

      analytics.people.set({ [key]: value });

      if (analyticsConfig.debugEnabled) {
        console.log(`📊 Analytics user property set: ${key}`, value);
      }
    } catch (error) {
      console.error("Failed to set analytics user property:", key, error);
    }
  },

  /**
   * Batches multiple user properties into a single Mixpanel people.set call,
   * instead of one network call per property.
   */
  setAnalyticsUserProperties: (
    properties: Record<string, string | number | boolean>
  ) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log("🚫 Analytics user properties update blocked:", {
            status: getAnalyticsStatus(),
            properties,
          });
        }
        return;
      }

      analytics.people.set(properties);

      if (analyticsConfig.debugEnabled) {
        console.log("📊 Analytics user properties set:", properties);
      }
    } catch (error) {
      console.error("Failed to set analytics user properties:", error);
    }
  },

  /**
   * Unions user property values with enhanced error handling
   */
  unionAnalyticsUserProperty: (key: string, values: string[]) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log(`🚫 Analytics user property union blocked: ${key}`, {
            status: getAnalyticsStatus(),
            values,
          });
        }
        return;
      }

      analytics.people.union({ [key]: values });

      if (analyticsConfig.debugEnabled) {
        console.log(`📊 Analytics user property union: ${key}`, values);
      }
    } catch (error) {
      console.error("Failed to union analytics user property:", key, error);
    }
  },

  /**
   * Appends value to user property with enhanced error handling
   */
  appendAnalyticsUserProperty: (key: string, value: string) => {
    try {
      if (!isAnalyticsReady()) {
        if (analyticsConfig.debugEnabled) {
          console.log(`🚫 Analytics user property append blocked: ${key}`, {
            status: getAnalyticsStatus(),
            value,
          });
        }
        return;
      }

      analytics.people.append({ [key]: value });

      if (analyticsConfig.debugEnabled) {
        console.log(`📊 Analytics user property appended: ${key}`, value);
      }
    } catch (error) {
      console.error("Failed to append analytics user property:", key, error);
    }
  },

  /**
   * Gets current analytics status for debugging and health checks
   */
  getStatus: () => {
    return getAnalyticsStatus();
  },

  /**
   * Checks if analytics is ready to receive events
   */
  isReady: () => {
    return isAnalyticsReady();
  },
};
